import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { agentCompleteVisit, agentDeclineVisit, getAssignedVisits } from '../api';
import MapLink from '../components/MapLink';
import DeliveryPayablesPanel from '../components/DeliveryPayablesPanel';
import MessageModal from '../components/MessageModal';
import { VISIT_RANGE_OPTIONS, visitInRange } from '../components/visitRanges';
import { visitAttention } from '../components/visitAttention';

// 03_requirements_missing.md story 5 - the site-visit agent's schedule. "Agent" is a
// `delivery`-role account (product decision - see the requirement doc). Read-plus-act only: the
// agent marks a visit done or declines it; the owner does the assigning.
export default function AgentDashboard() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [declineVisit, setDeclineVisit] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [completeVisit, setCompleteVisit] = useState(null);
  const [completeRemarks, setCompleteRemarks] = useState('');
  const [completeOutcome, setCompleteOutcome] = useState('visited');
  const [actionError, setActionError] = useState('');
  const [range, setRange] = useState('today'); // default to today's schedule, like jewelry's DeliveryDashboard

  const shownVisits = useMemo(() => visits.filter((v) => visitInRange(v, range)), [visits, range]);
  const shownVisitsPaged = usePagedArray(shownVisits, { resetKey: range });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setVisits(await getAssignedVisits());
      setError('');
    } catch {
      setError('Could not load your assigned visits.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openComplete = (visit) => {
    setCompleteVisit(visit);
    setCompleteRemarks('');
    setCompleteOutcome('visited');
    setActionError('');
  };

  const confirmComplete = async (e) => {
    e.preventDefault();
    const visit = completeVisit;
    if (!visit) return;
    if (!completeRemarks.trim()) { setActionError('Please add a short note about how the visit went.'); return; }
    setBusyId(visit._id);
    try {
      await agentCompleteVisit(visit._id, completeRemarks.trim(), completeOutcome);
      setCompleteVisit(null);
      await load();
    } catch (err) {
      setActionError(err?.response?.data?.msg || 'Unable to mark this visit as done.');
    } finally {
      setBusyId(null);
    }
  };

  const submitDecline = async (e) => {
    e.preventDefault();
    setBusyId(declineVisit._id);
    try {
      await agentDeclineVisit(declineVisit._id, declineReason.trim());
      setDeclineVisit(null);
      setDeclineReason('');
      await load();
    } catch (err) {
      setActionError(err?.response?.data?.msg || 'Unable to decline this visit.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0">My site visits</h3>
        <Form.Select
          size="sm" style={{ maxWidth: 180 }}
          value={range} onChange={(e) => setRange(e.target.value)}
          aria-label="Visit date range"
        >
          {VISIT_RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </Form.Select>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {shownVisitsPaged.total === 0 ? (
        <Alert variant="light">
          {visits.length === 0
            ? 'No site visits are assigned to you right now.'
            : 'No site visits in this range.'}
        </Alert>
      ) : (
        <>
        <div className="row g-3">
          {shownVisitsPaged.rows.map((v) => {
            const when = new Date(v.deliveryAssignment?.scheduledAt || v.startDate);
            const attention = visitAttention(v);
            return (
              <div className="col-md-6" key={v._id}>
                <Card className={attention ? 'rv-visit-attention' : ''}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <Card.Title as="h6" className="mb-0">{v.propertyName}</Card.Title>
                        <div className="small text-muted">{v.propertyLocation}</div>
                      </div>
                      <Badge bg="primary">{when.toLocaleString()}</Badge>
                    </div>
                    {attention && (
                      <Badge bg="dark" className="rv-visit-attention-badge mt-2">
                        <span aria-hidden="true">⚠</span> Reminder: {attention}
                      </Badge>
                    )}

                    <div className="small mt-2">
                      <div className="fw-semibold">{v.customerName}</div>
                      <div>
                        📞 <a href={`tel:${v.phoneNumber}`}>{v.phoneNumber}</a>
                      </div>
                      {v.notes && <div className="text-muted">Notes: {v.notes}</div>}
                      {v.deliveryAssignment?.notes && <div className="text-muted">From owner: {v.deliveryAssignment.notes}</div>}
                    </div>

                    {(v.siteAddress?.lat != null || v.siteAddress?.line1 || v.siteAddress?.formatted) ? (
                      <div className="mt-2">
                        <MapLink address={v.siteAddress} fallbackText={v.propertyLocation} title="Site address" />
                      </div>
                    ) : (
                      <div className="small text-muted mt-2">
                        No exact site address on this listing — {v.propertyLocation || 'location not set'}.
                      </div>
                    )}

                    <div className="d-flex gap-2 mt-2">
                      <Button size="sm" variant="success" disabled={busyId === v._id} onClick={() => openComplete(v)}>
                        Mark visited
                      </Button>
                      <Button size="sm" variant="outline-danger" disabled={busyId === v._id} onClick={() => { setDeclineVisit(v); setDeclineReason(''); }}>
                        Can't make it
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            );
          })}
        </div>
        <Pager {...shownVisitsPaged} />
        </>
      )}

      {/* delivery_person_payables.md - what property owners owe this agent per completed visit. */}
      <h4 className="mt-5 mb-3">Payments</h4>
      <DeliveryPayablesPanel />

      <Modal show={!!declineVisit} onHide={() => setDeclineVisit(null)} centered>
        <Form onSubmit={submitDecline}>
          <Modal.Header closeButton>
            <Modal.Title as="h5">Decline this visit?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="small text-muted">The owner is notified and can re-assign someone else.</p>
            <Form.Group controlId="agent-decline-reason">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control as="textarea" rows={2} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} maxLength={500} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setDeclineVisit(null)}>Keep it</Button>
            <Button variant="danger" type="submit" disabled={busyId === declineVisit?._id}>Decline</Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal show={!!completeVisit} onHide={() => setCompleteVisit(null)} centered>
        <Form onSubmit={confirmComplete}>
          <Modal.Header closeButton>
            <Modal.Title as="h5">Site visit report</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="small text-muted">
              {completeVisit?.propertyName} — {completeVisit?.customerName}. The owner sees your
              report on the visit.
            </p>
            <Form.Group className="mb-2" controlId="agent-complete-outcome">
              <Form.Label>Outcome</Form.Label>
              <Form.Select value={completeOutcome} onChange={(e) => setCompleteOutcome(e.target.value)}>
                <option value="visited">Visitor attended</option>
                <option value="no_show">Visitor did not show up</option>
                <option value="cancelled_on_site">Cancelled at the site</option>
                <option value="follow_up">Attended — follow-up needed</option>
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="agent-complete-remarks">
              <Form.Label>Remarks <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea" rows={3} required maxLength={2000}
                placeholder="How did the visit go? Visitor's interest, questions, next steps…"
                value={completeRemarks} onChange={(e) => setCompleteRemarks(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setCompleteVisit(null)}>Cancel</Button>
            <Button variant="success" type="submit" disabled={busyId === completeVisit?._id || !completeRemarks.trim()}>
              {busyId === completeVisit?._id ? 'Saving…' : 'Submit report'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      <MessageModal
        show={!!actionError}
        onHide={() => setActionError('')}
        title="Could not update visit"
        message={actionError}
      />
    </div>
  );
}
